import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useContext,
} from "react";

import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { IdType, SortingRule } from "react-table";
import { HvButton, HvGrid } from "@hitachivantara/uikit-react-core";
import {
  useHvTable,
  HvTableColumnConfig,
  useHvBulkActions,
  useHvPagination,
  useHvRowSelection,
  useHvSortBy,
  HvCellProps,
} from "@hitachivantara/uikit-react-lab";
import { Archive, Edit } from "@hitachivantara/uikit-react-icons";

import moment from "moment";

import SimpleTable, {
  textColumn,
  dateColumn,
  numberColumn,
  actionsColumn,
  badgeColumn,
  tagColumn,
  iconColumn,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  progressColumn,
} from "components/common/SimpleTable";
import { normalizeProgressBar } from "components/common/SimpleTable/renderers/ProgressColumnCell";

import {
  useIncidents,
  IncidentType,
  Severity,
  Status,
  Priority,
  useTimeSettings,
  archiveIncident,
  useStatus,
} from "lib/api";

import { formatToUTC } from "lib/utils/datetime";
import { NotificationContext } from "lib/context/NotificationContext";
import { useSnackbar } from "lib/provider/SnackbarProvider";

import IncidentTableControls from "../IncidentTableControls";
import ArchiveDialog from "../../common/ArchiveDialog";

interface IncidentDataRow extends Record<string, unknown> {
  id: string;
  name: string;
  incidentType?: IncidentType;
  priority?: Priority;
  severity?: Severity;
  statusId?: number;
  status?: Status;
  createdDate: string;
  assignee?: string;
  cameras: number;
  events: number;
}

const sortFieldMap = {
  name: "inc_name",
  incidentType: "incident_type.inc_type_name",
  priority: "fk_inc_priority_id",
  severity: "fk_inc_severity_id",
  status: "fk_inc_status_id",
  createdDate: "create_timestamp",
  assignee: "inc_assigned_user",
  events: "hv_entity_type_id",
  camera: "inc_nearest_cameras",
  updatedDate: "update_timestamp",
};

const DEFAULT_SORT = [
  {
    id: "createdDate",
    desc: true,
  },
];

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

interface IncidentFilters {
  incidentTypeFilter?: {
    filterValues: number[];
  };
  priority?: {
    filterValues: number[];
  };
  severity?: {
    filterValues: number[];
  };
  status?: {
    filterValues: number[];
  };
  assignee?: {
    filterValues: number[];
  };
}

const IncidentTable: React.FC = () => {
  const { t } = useTranslation("incident");
  const [tableData, setTableData] = useState<IncidentDataRow[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [searchKey, setSearchKey] = useState("");
  const history = useHistory();

  const query = useQuery();
  const incidentTypeId = query.get("incidentType");

  const [filter, setFilter] = useState<IncidentFilters>({
    incidentTypeFilter: {
      filterValues: incidentTypeId ? [Number(incidentTypeId)] : [],
    },
  });

  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { setShowBanner, setBannerMessage } = useContext(NotificationContext);

  const columns = useMemo(
    (): HvTableColumnConfig<IncidentDataRow>[] => [
      dateColumn({
        Header: t("columns.incidentUpdatedDate") as string,
        accessor: "updatedDate",
        style: { width: "15%" },
      }),
      dateColumn({
        Header: t("columns.incidentCreatedDate") as string,
        accessor: "createdDate",
        style: { width: "15%" },
      }),
      textColumn({
        Header: t("columns.incidentName") as string,
        accessor: "name",
        style: { width: "20%" },
      }),
      iconColumn(
        {
          Header: t("columns.incidentType") as string,
          accessor: "incidentType",
          style: { width: "15%" },
        },
        "appearance",
        "inc_type_name",
        8
      ),
      textColumn({
        Header: t("columns.incidentAssignee") as string,
        accessor: "assignee",
        style: { width: "10%" },
      }),
      badgeColumn(
        {
          Header: t("columns.incidentPriority") as string,
          accessor: "priority",
          style: { width: "10%" },
        },
        "P",
        "inc_priority_order",
        "prt_color",
        "prt_name"
      ),
      badgeColumn(
        {
          Header: t("columns.incidentSeverity") as string,
          accessor: "severity",
          style: { width: "10%" },
        },
        "S",
        "inc_severity_order",
        "svrt_color",
        "svrt_name"
      ),
      tagColumn(
        {
          Header: t("columns.incidentStatus") as string,
          accessor: "status",
          style: { width: "10%" },
        },
        "status_name",
        "status_color",
        "status_text_color"
      ),
      numberColumn({
        Header: t("columns.incidentEvents") as string,
        accessor: "events",
        style: { width: 100 },
        disableSortBy: true,
      }),
      numberColumn({
        Header: t("columns.incidentCameras") || undefined,
        accessor: "cameras",
        style: { width: 100 },
        disableSortBy: true,
      }),
      numberColumn({
        Header: t("columns.incidentSOP") || undefined,
        accessor: "sopCount",
        style: { width: 75 },
        disableSortBy: true,
      }),
      progressColumn(
        {
          Header: t("columns.incidentSOPProgress") || undefined,
          accessor: "completedSteps",
          style: { width: 125 },
          disableSortBy: true,
        },
        (row) => row.original.completedSteps as number,
        (row) => row.original.totalSteps as number,
        "secondary"
      ),
      textColumn({
        accessor: "sop_steps",
        style: { width: 45, paddingLeft: 0, textAlign: "right" },
        disableSortBy: true,
        Cell: ({ row }: HvCellProps<IncidentDataRow, string>) =>
          row.original.totalSteps
            ? `${normalizeProgressBar(
                row.original.completedSteps as number,
                row.original.totalSteps as number
              )}%`
            : "",
      }),
      actionsColumn({
        id: "edit",
        Cell: ({ row }: HvCellProps<IncidentDataRow>) => {
          return (
            <HvButton
              aria-label={t("labels.editIncidentButtonLabel")}
              icon
              onClick={() =>
                history.push(`/settings/incident/${row.original.id}`)
              }
            >
              <Edit />
            </HvButton>
          );
        },
      }),
    ],
    [t, history]
  );

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] =
    useState<SortingRule<IncidentDataRow>[]>(DEFAULT_SORT);
  const [selectedIds, setSelectedIds] = useState<
    Record<IdType<IncidentDataRow>, boolean>
  >({});

  const sortField = sortFieldMap[sortBy[0].id as keyof typeof sortFieldMap];

  const { timezone } = useTimeSettings();

  const [periodStart, setPeriodStart] = useState<Date | undefined>(undefined);
  const [periodEnd, setPeriodEnd] = useState<Date | undefined>(undefined);

  let dateFrom;
  let dateTo;
  if (periodStart != null && periodEnd != null) {
    dateFrom = formatToUTC(
      moment(periodStart).tz(timezone, true).startOf("day"),
      timezone
    );
    dateTo = formatToUTC(
      moment(periodEnd).tz(timezone, true).endOf("day"),
      timezone
    );
  }

  const {
    data: incidentsData,
    error: incidentsError,
    mutate,
  } = useIncidents(
    {
      pageNr: page + 1,
      pageSize: size,
      orderBy: encodeURIComponent(sortField),
      Order: encodeURIComponent(sortBy[0].desc ? "desc" : "asc"),
      dateFrom,
      dateTo,
      SearchKey: searchKey,
    },
    { suspense: tableData == null },
    filter as Record<string, unknown>
  );

  useEffect(() => {
    if (incidentsData) {
      setTableData(
        incidentsData.data.map((incident) => {
          return {
            id: `${incident.pk_inc_id}`,
            name: incident.inc_name,
            incidentType: incident.incident_type,
            priority: incident.priority,
            severity: incident.severity,
            statusId: incident.fk_inc_status_id,
            status: incident.status,
            createdDate: incident.create_timestamp,
            updatedDate: incident.update_timestamp,
            assignee: incident.inc_assigned_user
              ? `${incident.inc_assigned_user.name} ${incident.inc_assigned_user.surname}`
              : undefined,
            cameras: incident.inc_nearest_camera_ids?.length,
            events: incident.hv_correlated_entities?.length,
            sopCount: incident.inc_sop_map.length,
            completedSteps: incident.inc_overall_completed_steps,
            totalSteps: incident.inc_overall_steps,
          };
        })
      );
      setTotalCount(incidentsData.totalCount);
    }
  }, [incidentsData]);

  const { data: statusData } = useStatus({
    suspense: tableData == null,
  });
  const closedStatusId = useMemo(() => {
    return statusData?.data.find((s) => s.inc_status_type === "Closed")
      ?.pk_status_id;
  }, [statusData?.data]);

  const initialState = useMemo(() => {
    const entries = tableData?.map<[string, boolean]>((v) => [
      v.id.toString(),
      v.statusId !== closedStatusId,
    ]);

    return {
      pageIndex: page,
      pageSize: size,
      sortBy,
      selectedRowIds: selectedIds,
      lockedSelectionRowIds: (entries && Object.fromEntries(entries)) ?? {},
    };
  }, [tableData, page, size, sortBy, selectedIds, closedStatusId]);

  const labels = useMemo(
    () => ({
      selectRowCheckBoxAriaLabel: t("labels.selectIncidentCheckbox"),
    }),
    [t]
  );

  const bulkActions = useMemo(
    () => [
      {
        id: "archive",
        label: t("labels.bulkArchiveIncidentButton"),
        icon: <Archive />,
        onClick: () => setIsArchiveDialogOpen(true),
      },
    ],
    [t]
  );

  const onArchiveClose = useCallback(
    (result, message, ids) => {
      setIsArchiveDialogOpen(false);

      if (ids) {
        const newSelectedIds = {
          ...selectedIds,
        };

        ids.forEach((id: string) => {
          delete newSelectedIds[id];
        });
        setSelectedIds(newSelectedIds);
      }

      if (result && ids) {
        enqueueSnackbar(message, {
          variant: "success",
        });
        setPage(DEFAULT_PAGE);
        mutate();
      } else if (message) {
        setBannerMessage(message);
        setShowBanner(true);
        mutate();
      }
    },
    [enqueueSnackbar, mutate, selectedIds, setBannerMessage, setShowBanner]
  );

  const instance = useHvTable<IncidentDataRow>(
    {
      columns,
      data: tableData || [],
      getRowId: useCallback((v: IncidentDataRow): string => `${v.id}`, []),
      manualPagination: true,
      pageCount: Math.ceil((totalCount ?? 0) / (initialState?.pageSize ?? 1)),
      initialState,
      labels,
      aditivePageBulkSelection: true,
      subtractivePageBulkDeselection: true,
      manualSortBy: true,
      disableMultiSort: true,
      disableSortRemove: true,
      disableCreateExpandButton: true,
    },
    useHvSortBy,
    useHvPagination,
    useHvRowSelection,
    useHvBulkActions
  );

  const {
    state: { selectedRowIds, pageIndex, pageSize, sortBy: sortByFromTable },
  } = instance;

  useEffect(() => setPage?.(pageIndex), [pageIndex, setPage]);
  useEffect(() => setSize?.(pageSize), [pageSize, setSize]);
  useEffect(() => {
    setSortBy?.(sortByFromTable);
    setPage?.(0);
  }, [sortByFromTable, setSortBy, setPage]);
  useEffect(() => setSelectedIds(selectedRowIds), [selectedRowIds]);

  const onChangeDates = useCallback(
    (startDate?: Date, endDate?: Date) => {
      if (startDate != null && endDate != null) {
        setPeriodStart(startDate);
        setPeriodEnd(endDate);
      } else {
        setPeriodStart(undefined);
        setPeriodEnd(undefined);
      }

      // go back to first page
      instance.gotoPage(DEFAULT_PAGE);
    },
    [instance]
  );

  const onSearchHandler = (value: string) => {
    setSearchKey(value);
    instance.gotoPage(DEFAULT_PAGE);
  };

  const onFilterHandler = (value: number[][]) => {
    setFilter({
      incidentTypeFilter: {
        filterValues: value[0] || [],
      },
      priority: {
        filterValues: value[1] || [],
      },
      severity: {
        filterValues: value[2] || [],
      },
      status: {
        filterValues: value[3] || [],
      },
      assignee: {
        filterValues: value[4] || [],
      },
    });
    instance.gotoPage(DEFAULT_PAGE);
  };

  const filterValues = useMemo(() => {
    const { incidentTypeFilter, priority, severity, status, assignee } = filter;
    return [
      incidentTypeFilter?.filterValues || [],
      priority?.filterValues || [],
      severity?.filterValues || [],
      status?.filterValues || [],
      assignee?.filterValues || [],
    ];
  }, [filter]);

  return tableData != null ? (
    <HvGrid container spacing={2}>
      <HvGrid item xs={12}>
        <IncidentTableControls
          startDate={periodStart}
          endDate={periodEnd}
          onDateRangeChange={onChangeDates}
          onSearch={onSearchHandler}
          filters={filterValues}
          onFilter={onFilterHandler}
        />
      </HvGrid>
      <HvGrid item xs={12}>
        <ArchiveDialog
          scope="incident"
          isOpen={isArchiveDialogOpen}
          promise={archiveIncident}
          onClose={onArchiveClose}
          ids={Object.keys(selectedRowIds)}
        />
        <SimpleTable
          instance={instance}
          bulkActions={bulkActions}
          totalCount={totalCount}
          loading={(!incidentsData && !incidentsError) || tableData === []}
        />
      </HvGrid>
    </HvGrid>
  ) : (
    <></>
  );
};
export default IncidentTable;
